import { useState, useCallback } from "react";
import { Upload, FileText, Loader2, AlertCircle, Check, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import type { ParseInvoiceRequest, ParseInvoiceResponse } from "@/types/edge-functions";

interface ParsedInvoice {
  payment_amount: number | null;
  payment_due_date: string | null;
  supplier_name: string | null;
  supplier_email: string | null;
}

interface InvoiceUploaderProps {
  onInvoiceParsed: (
    data: ParsedInvoice & { file_url?: string; file_name?: string },
    needsManualInput: boolean
  ) => void;
  isUploading: boolean;
  setIsUploading: (loading: boolean) => void;
  onBatchComplete?: () => void;
}

interface FileUploadStatus {
  file: File;
  status: "pending" | "uploading" | "parsing" | "success" | "error" | "needs_input";
  error?: string;
  parsedData?: ParsedInvoice & { file_url?: string; file_name?: string };
}

export function InvoiceUploader({
  onInvoiceParsed,
  isUploading,
  setIsUploading,
  onBatchComplete,
}: InvoiceUploaderProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);

  const processFile = async (file: File): Promise<FileUploadStatus> => {
    try {
      // Upload file to Supabase storage first
      const fileExt = file.name.split(".").pop();
      const uniqueFileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `invoices/${uniqueFileName}`;

      const { error: uploadError } = await supabase.storage
        .from("invoices")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        throw new Error(`Failed to upload file: ${uploadError.message}`);
      }

      // Get the public URL for the file
      const { data: urlData } = supabase.storage.from("invoices").getPublicUrl(filePath);

      const fileUrl = urlData.publicUrl;

      // Convert file to base64 for parsing
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = (reader.result as string).split(",")[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const base64Content = await base64Promise;

      // Call edge function to parse invoice with retry logic
      const request: ParseInvoiceRequest = {
        pdfBase64: base64Content,
        fileName: file.name,
      };

      const response = await invokeEdgeFunction<ParseInvoiceResponse>("parse-invoice", request, {
        strategy: "immediate",
        maxRetries: 2,
        retryDelay: 300,
        idempotent: false,
      });

      const raw = response as unknown as { parsed?: ParsedInvoice } & ParsedInvoice;
      const parsed: ParsedInvoice = raw.parsed ?? raw;
      const needsManualInput =
        !parsed.payment_amount ||
        !parsed.payment_due_date ||
        !parsed.supplier_name ||
        !parsed.supplier_email;

      return {
        file,
        status: needsManualInput ? "needs_input" : "success",
        parsedData: {
          ...parsed,
          file_url: fileUrl,
          file_name: file.name,
        },
      };
    } catch (err) {
      return {
        file,
        status: "error",
        error: err instanceof Error ? err.message : "Failed to process invoice",
      };
    }
  };

  const handleFiles = useCallback(
    async (files: File[]) => {
      const pdfFiles = files.filter((f) => f.type === "application/pdf");

      if (pdfFiles.length === 0) {
        setError("Please upload PDF files only");
        return;
      }

      if (pdfFiles.length !== files.length) {
        setError(`${files.length - pdfFiles.length} non-PDF file(s) were skipped`);
      } else {
        setError(null);
      }

      setIsUploading(true);

      // Initialize file statuses
      const initialStatuses: FileUploadStatus[] = pdfFiles.map((file) => ({
        file,
        status: "pending",
      }));
      setFileStatuses(initialStatuses);

      // Process files sequentially to avoid overwhelming the server
      const results: FileUploadStatus[] = [];
      for (let i = 0; i < pdfFiles.length; i++) {
        const file = pdfFiles[i];

        // Update status to uploading
        setFileStatuses((prev) =>
          prev.map((s, idx) => (idx === i ? { ...s, status: "uploading" } : s))
        );

        const result = await processFile(file);
        results.push(result);

        // Update status with result
        setFileStatuses((prev) => prev.map((s, idx) => (idx === i ? result : s)));

        // If this invoice was fully parsed, trigger the callback
        if (result.status === "success" && result.parsedData) {
          onInvoiceParsed(result.parsedData, false);
        }
      }

      // Check if any need manual input - handle the first one
      const needsInput = results.find((r) => r.status === "needs_input");
      if (needsInput && needsInput.parsedData) {
        onInvoiceParsed(needsInput.parsedData, true);
      }

      setIsUploading(false);

      // Clear file statuses after a delay if all successful
      const allSuccessful = results.every((r) => r.status === "success");
      if (allSuccessful && results.length > 0) {
        setTimeout(() => {
          setFileStatuses([]);
          onBatchComplete?.();
        }, 2000);
      }
    },
    [onInvoiceParsed, setIsUploading, onBatchComplete]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(Array.from(e.dataTransfer.files));
      }
    },
    [handleFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(Array.from(e.target.files));
      }
    },
    [handleFiles]
  );

  const completedCount = fileStatuses.filter((s) => s.status === "success").length;
  const totalCount = fileStatuses.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <Card className="glass-card p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-display font-semibold text-foreground mb-2">
          Upload Invoices
        </h2>
        <p className="text-muted-foreground">
          Drop your PDF invoices here or click to browse (multiple files supported)
        </p>
      </div>

      <div
        className={`
          relative border-2 border-dashed rounded-xl p-12 transition-all duration-200 cursor-pointer
          ${dragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/50"}
          ${isUploading ? "pointer-events-none opacity-70" : ""}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !isUploading && document.getElementById("invoice-input")?.click()}
      >
        <input
          id="invoice-input"
          type="file"
          accept=".pdf"
          multiple
          onChange={handleInputChange}
          className="hidden"
          disabled={isUploading}
        />

        <div className="flex flex-col items-center gap-4">
          {isUploading ? (
            <>
              <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center shadow-primary">
                <Loader2 className="w-8 h-8 text-primary-foreground animate-spin" />
              </div>
              <div className="text-center w-full max-w-xs">
                <p className="font-medium text-foreground mb-2">
                  Processing {totalCount} file{totalCount !== 1 ? "s" : ""}...
                </p>
                <Progress value={progressPercent} className="h-2" />
                <p className="text-sm text-muted-foreground mt-2">
                  {completedCount} of {totalCount} complete
                </p>
              </div>
            </>
          ) : (
            <>
              <div
                className={`
                w-16 h-16 rounded-full flex items-center justify-center transition-all
                ${dragActive ? "gradient-primary shadow-primary" : "bg-muted"}
              `}
              >
                <Upload
                  className={`w-8 h-8 ${dragActive ? "text-primary-foreground" : "text-muted-foreground"}`}
                />
              </div>
              <div className="text-center">
                <p className="font-medium text-foreground">Drag & drop your invoice PDFs</p>
                <p className="text-sm text-muted-foreground mt-1">or click to browse files</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* File status list */}
      {fileStatuses.length > 0 && (
        <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
          {fileStatuses.map((status, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm truncate flex-1">{status.file.name}</span>
              {status.status === "pending" && (
                <span className="text-xs text-muted-foreground">Waiting...</span>
              )}
              {(status.status === "uploading" || status.status === "parsing") && (
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              )}
              {status.status === "success" && <Check className="w-4 h-4 text-success" />}
              {status.status === "needs_input" && (
                <span className="text-xs text-warning">Needs review</span>
              )}
              {status.status === "error" && <X className="w-4 h-4 text-destructive" />}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
    </Card>
  );
}
